/**
 * Device Fingerprinting Utility
 * Generates a unique device identifier based on browser and system characteristics
 */

// Generate device fingerprint based on multiple factors
export const generateDeviceFingerprint = async () => {
  const components = [];

  // Browser information
  components.push(navigator.userAgent);
  components.push(navigator.language);
  components.push(navigator.platform);
  components.push(navigator.hardwareConcurrency || 'unknown');
  components.push(navigator.deviceMemory || 'unknown');
  
  // Screen information
  components.push(window.screen.width);
  components.push(window.screen.height);
  components.push(window.screen.colorDepth);
  components.push(window.screen.pixelDepth);
  
  // Timezone
  components.push(Intl.DateTimeFormat().resolvedOptions().timeZone);
  components.push(new Date().getTimezoneOffset());
  
  // Canvas fingerprint
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillStyle = '#f60';
  ctx.fillRect(125, 1, 62, 20);
  ctx.fillStyle = '#069';
  ctx.fillText('Device ID', 2, 15);
  components.push(canvas.toDataURL());
  
  // WebGL fingerprint
  try {
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        components.push(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL));
        components.push(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
      }
    }
  } catch (e) {
    components.push('webgl-error');
  }
  
  // Combine all components and hash
  const fingerprint = await hashString(components.join('|||'));
  return fingerprint;
};

// Simple hash function - fallback for non-HTTPS environments
const hashString = async (str) => {
  // Try to use SubtleCrypto if available (HTTPS or localhost)
  if (window.crypto && window.crypto.subtle) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(str);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      return hashHex;
    } catch (e) {
      console.warn('SubtleCrypto not available, using fallback hash');
    }
  }
  
  // Fallback: Simple hash function for non-HTTPS
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16).padStart(16, '0');
};

// Get or create device ID
export const getDeviceId = async () => {
  let deviceId = localStorage.getItem('device_id');
  
  if (!deviceId) {
    console.log('Generating new device ID...');
    deviceId = await generateDeviceFingerprint();
    console.log('Generated device ID:', deviceId);
    localStorage.setItem('device_id', deviceId);
    localStorage.setItem('deviceId', deviceId); // Store both keys
  }
  
  console.log('Returning device ID:', deviceId);
  return deviceId;
};

// Check if device is trusted (stored in localStorage)
export const isDeviceTrusted = (userName, companyCode) => {
  const trustedDevices = JSON.parse(localStorage.getItem('trusted_devices') || '{}');
  const key = `${companyCode}_${userName}`;
  return trustedDevices[key] === true;
};

// Mark device as trusted
export const markDeviceAsTrusted = (userName, companyCode) => {
  const trustedDevices = JSON.parse(localStorage.getItem('trusted_devices') || '{}');
  const key = `${companyCode}_${userName}`;
  trustedDevices[key] = true;
  localStorage.setItem('trusted_devices', JSON.stringify(trustedDevices));
};

// Remove device trust (on logout from different device)
export const removeDeviceTrust = (userName, companyCode) => {
  const trustedDevices = JSON.parse(localStorage.getItem('trusted_devices') || '{}');
  const key = `${companyCode}_${userName}`;
  delete trustedDevices[key];
  localStorage.setItem('trusted_devices', JSON.stringify(trustedDevices));
};

// Clear all device data
export const clearDeviceData = () => {
  localStorage.removeItem('device_id');
  localStorage.removeItem('trusted_devices');
};
